import { api } from "@/lib/api";
import {
  CreateFulfillment,
  createFulfillmentToDto,
  FulfillmentDtoSchema,
  fulfillmentFromDto,
  ReservationServiceWithFulfillments,
  ReservationServiceWithfulfillmentsDtoSchema,
  reservationServiceWithFulfillmentsFromDto,
  reservationServiceWithFulfillmentsToDto,
} from "@/models";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../use-toast";
import { getAllReservationServicesWithFulfillmentsQueryOptions } from "./get.hooks";

export function useCreateReservationServiceFulfillment(opts?: {
  onSuccess?: (guide: ReservationServiceWithFulfillments) => void;
  onError?: () => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({
      service,
      fulfillment: createFulfillment,
    }: {
      fulfillment: CreateFulfillment;
      service: ReservationServiceWithFulfillments;
    }) => {
      const fulfillmentResult = await api.post(
        "/api/fulfillments",
        createFulfillmentToDto(createFulfillment),
        FulfillmentDtoSchema,
      );

      const fulfillment = fulfillmentFromDto(fulfillmentResult);

      const fulfillments = [...(service.fulfillments ?? []), fulfillment];

      const updateServiceDto = await api.put(
        "/api/reservation-services",
        reservationServiceWithFulfillmentsToDto({
          ...service,
          fulfillments,
          reservationClients: service.reservationClients
        }),
        ReservationServiceWithfulfillmentsDtoSchema,
      );

      const newService =
        reservationServiceWithFulfillmentsFromDto(updateServiceDto);

      newService.fulfillments = fulfillments;
      newService.balance =
        (newService.price ?? 0) -
        fulfillments.reduce((acc, item) => acc + (item.value ?? 0), 0);
      newService.isPayed = false;
      return newService;
    },
    onSuccess: (newReservationService) => {
      const queryKey = getAllReservationServicesWithFulfillmentsQueryOptions(
        newReservationService.reservationId?.toString() ?? "",
      ).queryKey;
      queryClient.setQueryData<ReservationServiceWithFulfillments[]>(
        queryKey,
        (oldData) =>
          oldData?.map((d) =>
            d.id === newReservationService.id ? newReservationService : d,
          ),
      );
      toast({
        title: "toast.fulfillmentCreated",
        variant: "success",
      });
      opts?.onSuccess?.(newReservationService);
    },
    onError: () => {
      toast({
        title: "toast.createFulfillmentError",
        variant: "destructive",
      });
      opts?.onError?.();
    },
  });
}
